import { Transaction } from '@tanstack/db';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { VoorschriftSchema } from '@/api';

type SelectedVoorschrift = {
  id: string;
  type: 'voorschrift';
};

type SelectedSubparagraaf = {
  id: string;
  type: 'subparagraaf';
};

type SelectedParagraaf = {
  id: string;
  type: 'paragraaf';
};

type SelectedHoofdstuk = {
  id: string;
  type: 'hoofdstuk';
};

type SelectedObject =
  | SelectedVoorschrift
  | SelectedSubparagraaf
  | SelectedParagraaf
  | SelectedHoofdstuk;

export enum EditorView {
  DEFAULT = 'default',
  ADD = 'add',
  DRAGNDROP = 'dragndrop',
  RECOVER = 'recover',
}

export enum SidebarLeftTab {
  INDEX = 'index',
  FILTER = 'filter',
}

export type MultiSelectVoorschrift = {
  hoofdstukId: string;
  paragraafId: string;
  voorschriftId: string;
};

export interface EditorStoreState {
  view: EditorView;
  isSidebarLeftOpen: boolean;
  sidebarLeftTabId: SidebarLeftTab;
  selectedObject?: SelectedObject;
  includeCheckedComments: boolean;
  isLabelBeheerMode: boolean;
  multiSelectVoorschriften: Map<
    VoorschriftSchema['ID'],
    MultiSelectVoorschrift
  >;
  multiSelectVoorschriftIdLog: Set<string>;
  persistingTransactionsIds: Set<string>;
}

export interface EditorStoreActions {
  setView: (view: EditorStoreState['view']) => void;
  setSidebarLeftOpen: (isOpen: boolean) => void;
  setSidebarLeftTabId: (tabId: SidebarLeftTab) => void;
  reset: () => void;
  selectObject: (selectedObject: SelectedObject) => void;
  clearSelectedObject: () => void;
  setIncludeCheckedComments: (includeCheckedComments: boolean) => void;
  enterLabelBeheerMode: () => void;
  exitLabelBeheerMode: () => void;
  addMultiSelectVoorschrift: (
    multiSelectVoorschrift: MultiSelectVoorschrift
  ) => void;
  removeMultiSelectVoorschrift: (voorschriftId: string) => void;
  clearMultiSelectVoorschriften: () => void;
  addPersistingTransaction: (
    transaction: Transaction<Record<string, unknown>>
  ) => void;
}

export const EDITOR_STORE_INITIAL_STATE: EditorStoreState = {
  view: EditorView.DEFAULT,
  isSidebarLeftOpen: false,
  sidebarLeftTabId: SidebarLeftTab.INDEX,
  selectedObject: undefined,
  includeCheckedComments: false,
  isLabelBeheerMode: false,
  multiSelectVoorschriften: new Map(),
  multiSelectVoorschriftIdLog: new Set(),
  persistingTransactionsIds: new Set(),
};

export const useEditorStore = create<EditorStoreState & EditorStoreActions>()(
  persist(
    (set) => ({
      ...EDITOR_STORE_INITIAL_STATE,
      setView: (view) => set({ view }),
      setSidebarLeftOpen: (isSidebarLeftOpen) => set({ isSidebarLeftOpen }),
      setSidebarLeftTabId: (sidebarLeftTabId) => set({ sidebarLeftTabId }),
      reset: () => set({ ...EDITOR_STORE_INITIAL_STATE }),
      selectObject: (selectedObject) =>
        set((state) => {
          if (state.isLabelBeheerMode) return {};

          return {
            selectedObject,
            multiSelectVoorschriften: new Map(),
          };
        }),
      clearSelectedObject: () => set({ selectedObject: undefined }),
      setIncludeCheckedComments: (includeCheckedComments) =>
        set({ includeCheckedComments }),
      enterLabelBeheerMode: () =>
        set({
          isLabelBeheerMode: true,
          selectedObject: undefined,
          multiSelectVoorschriften: new Map(),
          multiSelectVoorschriftIdLog: new Set(),
        }),
      exitLabelBeheerMode: () =>
        set({
          isLabelBeheerMode: false,
          multiSelectVoorschriften: new Map(),
          multiSelectVoorschriftIdLog: new Set(),
        }),
      addMultiSelectVoorschrift: (multiSelectVoorschrift) =>
        set((state) => {
          const next = new Map(state.multiSelectVoorschriften);
          next.set(
            multiSelectVoorschrift.voorschriftId,
            multiSelectVoorschrift
          );

          const nextIds = new Set(state.multiSelectVoorschriftIdLog);
          nextIds.add(multiSelectVoorschrift.voorschriftId);

          return {
            multiSelectVoorschriften: next,
            multiSelectVoorschriftIdLog: nextIds,
          };
        }),
      removeMultiSelectVoorschrift: (voorschriftId) =>
        set((state) => {
          const next = new Map(state.multiSelectVoorschriften);
          next.delete(voorschriftId);

          return { multiSelectVoorschriften: next };
        }),
      clearMultiSelectVoorschriften: () =>
        set({ multiSelectVoorschriften: new Map() }),
      addPersistingTransaction: (transaction) => {
        if (!transaction.id || !transaction.isPersisted.promise) return;

        set((state) => {
          const newSet = new Set(state.persistingTransactionsIds);
          newSet.add(transaction.id);

          return {
            persistingTransactionsIds: newSet,
          };
        });

        transaction.isPersisted.promise.finally(() => {
          set((state) => {
            const newSet = new Set(state.persistingTransactionsIds);
            newSet.delete(transaction.id);

            return {
              persistingTransactionsIds: newSet,
            };
          });
        });
      },
    }),
    {
      name: 'editor-store',
      partialize: (state) => ({
        isSidebarLeftOpen: state.isSidebarLeftOpen,
      }),
    }
  )
);
